/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import { type ReactNode, useEffect, useState } from "react";

import { lineHeights, spacing } from "../../../../../global/emotion";
import { useDynamicViewport } from "../../../../hooks/useDynamicViewport";
import { VIEWPORT_TABLET } from "../../../../lib/constants/viewports/viewportTablet";
import type { DropdownMenuHeaderProps } from "../DropdownMenuHeader/DropdownMenuHeader.emotion";
import { DropdownMenuHeader } from "../DropdownMenuHeader/DropdownMenuHeader.emotion";
import type { ModalHeaderProps } from "../ModalHeader/ModalHeader.emotion";
import { ModalHeader } from "../ModalHeader/ModalHeader.emotion";

const emptyStyle: Interpolation = {
  // default style for Modal Header, which we need to account for in visual space
  height: `calc(${spacing.xl} + ${spacing.m} + ${lineHeights.titleM})`,
};

export type ModalDropdownMenuHeaderProps = {
  children?: ReactNode;
  dropdownMenuHeaderProps?: DropdownMenuHeaderProps;
  modalHeaderProps?: ModalHeaderProps;
  minDesktopWidth?: number;
};

export const ModalDropdownMenuHeader = ({
  children,
  dropdownMenuHeaderProps = {},
  modalHeaderProps = {},
  minDesktopWidth = VIEWPORT_TABLET,
}: ModalDropdownMenuHeaderProps) => {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  const isDesktopViewport = useDynamicViewport({
    minimumWidth: minDesktopWidth,
    defaultState: undefined,
  });

  if (!isMounted) return <div css={emptyStyle} />;

  if (isDesktopViewport)
    return (
      <DropdownMenuHeader {...dropdownMenuHeaderProps}>
        {dropdownMenuHeaderProps.children || children}
      </DropdownMenuHeader>
    );

  return (
    <ModalHeader {...modalHeaderProps}>
      {modalHeaderProps.children || children}
    </ModalHeader>
  );
};
